import type {
  McpCapability,
  McpDiscoveryResult,
  McpProbeResult,
  McpServer,
  RemoteAgent,
  RiskClass,
  SystemAccessInput,
  UpdateMcpCapabilityRequest,
  UpdateMcpServerRequest,
  UpdateRemoteAgentRequest
} from '@ralph/contracts'
import { api, apiGet, apiSend } from '@/api/client.ts'

/**
 * Connected systems — the administrative surface for everything this platform may reach.
 *
 * Named for the product word rather than the protocol one. "MCP" appears in exactly two
 * places in this application: the protocol line of a system's diagnostics, where it is a
 * fact an operator needs, and nowhere else. Everywhere a person reads about a *system* and
 * what it can *do*.
 *
 * Every path here is `/api/admin/mcp/*` because that is what the server calls them, and
 * renaming a route to match a UI word is how a URL stops meaning anything.
 */

export type PreflightResponse = {
  result: McpProbeResult
  /** A registry name proposed from the server's own identity. Empty when nothing usable. */
  suggestedName: string
}

export type ServerListResponse = {
  items: McpServer[]
  protocolVersion: string
  enabled: boolean
}

/**
 * Diagnose an address before anything is registered.
 *
 * The one call in this file that reaches a third party, and the reason the connect flow can
 * open with a single field: everything it needs to ask next — a name, whether a credential
 * is wanted, which kind — is in the answer.
 */
export const preflightSystem = (url: string, tokenRef?: string): Promise<PreflightResponse> =>
  apiSend<PreflightResponse>('/api/admin/mcp/preflight', 'POST', { url, ...(tokenRef ? { tokenRef } : {}) })

export const fetchSystems = (): Promise<ServerListResponse> => apiGet<ServerListResponse>('/api/admin/mcp')

export type AuthorizeMode = 'delegated' | 'service_account'

export type AuthorizeStart = {
  /** Where to send the browser. A full top-level navigation, never a fetch. */
  authorizeUrl: string
  issuer: string
  registration: 'preregistered' | 'cimd' | 'dcr'
}

/**
 * Begin a sign-in at the authority that guards a system.
 *
 * Returns a URL instead of following it, because a `302` answered to `fetch` would be
 * followed by `fetch` and land somebody else's HTML in a JSON parser. The caller performs a
 * real top-level navigation, which is also what makes the authorization server's own
 * cookies and the browser's address bar work the way a person expects when they are being
 * asked to approve something.
 */
export const authorizeSystem = (id: string, mode: AuthorizeMode, challengedScope?: string): Promise<AuthorizeStart> =>
  apiSend<AuthorizeStart>(`/api/admin/mcp/${encodeURIComponent(id)}/authorize`, 'POST', {
    mode,
    ...(challengedScope ? { challengedScope } : {})
  })

export type MyConnection = {
  id: string
  serverId: string
  serverName: string
  displayName: string
  mode: AuthorizeMode
  scope: string
  authorizedAt: string
  expiresAt: string | null
  lastUsedAt: string | null
  needsReconnect: boolean
}

export const fetchMyConnections = (): Promise<{ items: MyConnection[] }> =>
  apiGet<{ items: MyConnection[] }>('/api/me/connections')

export const disconnectMine = (connectionId: string): Promise<unknown> =>
  apiSend<unknown>(`/api/me/connections/${encodeURIComponent(connectionId)}`, 'DELETE')

export const fetchSystem = (id: string): Promise<McpServer> =>
  apiGet<McpServer>(`/api/admin/mcp/${encodeURIComponent(id)}`)

export type CreateSystemInput = {
  name: string
  description: string
  url: string
  authKind: 'none' | 'bearer'
  tokenRef?: string | null
  trustTier: 'untrusted' | 'reviewed' | 'first_party'
  enabled: boolean
  /**
   * Who can use it (PRD-26 D-25). Everyone by default, which is safe at this moment: nothing a
   * new system offers is switched on until an administrator reviews it, so "everyone" reaches
   * nothing yet — and the system's page is where it is narrowed before anything is.
   */
  access?: SystemAccessInput
}

export const createSystem = (input: CreateSystemInput): Promise<McpServer> =>
  apiSend<McpServer>('/api/admin/mcp', 'POST', input)

/**
 * `If-Match` carries the version that was read.
 *
 * Optimistic concurrency the same way the agent store does it: two administrators editing
 * one system's trust tier is not a hypothetical on a page that invites re-review, and a
 * silent clobber of somebody's risk decision is the one outcome worth a `409` for.
 */
export const updateSystem = (id: string, version: number, patch: UpdateMcpServerRequest): Promise<McpServer> =>
  api<McpServer>(`/api/admin/mcp/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'if-match': String(version) },
    body: JSON.stringify(patch)
  })

export const deleteSystem = (id: string): Promise<unknown> =>
  apiSend<unknown>(`/api/admin/mcp/${encodeURIComponent(id)}`, 'DELETE')

export const probeSystem = (id: string): Promise<McpProbeResult> =>
  apiSend<McpProbeResult>(`/api/admin/mcp/${encodeURIComponent(id)}/probe`, 'POST')

export const discoverSystem = (id: string): Promise<McpDiscoveryResult> =>
  apiSend<McpDiscoveryResult>(`/api/admin/mcp/${encodeURIComponent(id)}/discover`, 'POST')

export const fetchCapabilities = (id: string): Promise<{ items: McpCapability[] }> =>
  apiGet<{ items: McpCapability[] }>(`/api/admin/mcp/${encodeURIComponent(id)}/capabilities`)

export const updateCapability = (capabilityId: string, patch: UpdateMcpCapabilityRequest): Promise<McpCapability> =>
  apiSend<McpCapability>(`/api/admin/mcp/capabilities/${encodeURIComponent(capabilityId)}`, 'PATCH', patch)

export type BulkCapabilityChange = { capabilityId: string; risk: RiskClass }

/**
 * Several capabilities at once, one request each.
 *
 * Deliberately not a batch endpoint. A partial failure has to be reportable per capability —
 * one quarantined row in a selection of twelve must not silently take the other eleven with
 * it, and must not be reported as "eleven succeeded" either. `allSettled` keeps each outcome
 * attached to the capability it belongs to, and the caller renders the ones that refused.
 *
 * What travels back is the REJECTION, not its English sentence. Flattening it to `.message`
 * here would discard the `messageKey` the server attached to it, and this module has no
 * catalogue to render one with anyway — so the refusal stays an error object all the way to the
 * component, which hands it to `describeApiError` (PRD-27 D-4, F-1).
 */
export const applyCapabilities = async (
  changes: BulkCapabilityChange[]
): Promise<Array<{ capabilityId: string; ok: boolean; error?: unknown }>> => {
  const settled = await Promise.allSettled(
    changes.map((change) => updateCapability(change.capabilityId, { enabled: true, risk: change.risk }))
  )
  return changes.map((change, index) => {
    const outcome = settled[index]
    if (outcome?.status === 'fulfilled') return { capabilityId: change.capabilityId, ok: true }
    return {
      capabilityId: change.capabilityId,
      ok: false,
      error: outcome?.status === 'rejected' ? outcome.reason : undefined
    }
  })
}

// ---------------------------------------------------------------------------
// Remote agents (PRD-26 GRP-061)
// ---------------------------------------------------------------------------

/**
 * Remote agents, as the Systems tab's second section reads them.
 *
 * Only who can use one is edited here. Registering a remote agent, reviewing its card and turning
 * it on stay on the administration API (PRD-05): those are decisions about a publisher, made with
 * the card in front of you, and this section is about which of this platform's people may reach it.
 */
export const fetchRemoteAgents = (): Promise<{ items: RemoteAgent[]; nextCursor: string | null }> =>
  apiGet<{ items: RemoteAgent[]; nextCursor: string | null }>('/api/admin/a2a/agents')

/** Bound to the version that was read, like a connected system: a silent clobber is the outcome worth a 412. */
export const updateRemoteAgent = (
  id: string,
  version: number,
  patch: UpdateRemoteAgentRequest
): Promise<{ agent: RemoteAgent }> =>
  apiSend<{ agent: RemoteAgent }>(`/api/admin/a2a/agents/${encodeURIComponent(id)}`, 'PATCH', patch, {
    'if-match': String(version)
  })
